import { useParams } from 'react-router'
import {
  readStorageItems,
  removeFromStorageItemsList,
  RouteParams,
} from '@sketch/modules-common'
import { useCreateProject } from '../../projects/hooks/useCreateProject'
import {
  GetStorageItemsDocument,
  GetStorageItemsQueryVariables,
  StorageItemFragment,
  useGetProjectDetailsQuery,
} from '@sketch/gql-types'
import { useGenerateFolderName } from './useGenerateFolderName'
import produce from 'immer'

interface UseDropStorageItemsIntoShareProps {
  projectIdentifier?: string
  storageItemsQueryVariables: GetStorageItemsQueryVariables
}

export const useDropStorageItemsIntoShare = ({
  projectIdentifier,
  storageItemsQueryVariables,
}: UseDropStorageItemsIntoShareProps) => {
  const { workspaceId } =
    useParams<RouteParams<'WORKSPACE_PROJECT' | 'WORKSPACE_DRAFTS'>>()

  // We need the project info for the optimistic response, this data it's already in cache
  const { data: currentProject } = useGetProjectDetailsQuery({
    variables: { projectIdentifier: projectIdentifier || '' },
    skip: !projectIdentifier,
  })

  const { createProject } = useCreateProject()

  const generateFolderName = useGenerateFolderName(
    storageItemsQueryVariables,
    projectIdentifier
  )

  // Creates a nested project with the provided storage items
  const dropStorageItemsToNestedProject = async (
    storageItems: StorageItemFragment[]
  ) => {
    // we need a valid project id to create a nested project
    if (!projectIdentifier) {
      return
    }

    if (storageItems.length === 0) {
      return
    }

    const newFolderName = generateFolderName()

    // Create nested project with all storage items inside
    createProject({
      variables: {
        workspaceId,
        parentProjectIdentifier: projectIdentifier,
        name: newFolderName,
        description: '',
        storageItems: storageItems.map(item => ({
          identifier: item.identifier,
          type: item.__typename === 'Share' ? 'SHARE' : 'PROJECT',
        })),
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        createWorkspaceProject: {
          __typename: 'CreateWorkspaceProjectResponse',
          project: {
            isOptimistic: true,
            __typename: 'Project',
            archivedAt: null,
            deletedAt: null,
            description: null,
            userCanLeave: false,
            projectPrivacyIcon: 'WORKSPACE_MEMBERS',
            identifier: 'new-project-id',
            name: newFolderName,
            parentProjectIdentifier: projectIdentifier,
            pinnedByCurrentUserAt: null,
            projectType: 'STANDARD',
            parentProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: currentProject?.project?.name ?? '',
              nestedProjectsCount:
                (currentProject?.project?.nestedProjectsCount ?? 0) + 1,
              sharesCount:
                (currentProject?.project?.sharesCount ?? 0) -
                storageItems.length,
            },
            topLevelProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: '',
              workspaceAccessLevel:
                currentProject?.project?.workspaceAccessLevel ?? 'NONE',
            },
            workspaceAccessLevel:
              currentProject?.project?.workspaceAccessLevel ?? 'NONE',
          },
        },
      },
      update: (cache, { data }) => {
        // The strategy is to create the new project as quickly as possible to
        // give a sense of speed (that's possible because we are using an
        // optimistic response), and after the mutation is completed, the
        // nested project will be filled with the real data.
        const cachedStorageItems = readStorageItems(
          cache,
          storageItemsQueryVariables
        )

        const newProject = data?.createWorkspaceProject.project

        const newStorageItems = produce(cachedStorageItems, draft => {
          if (
            !draft?.project?.storageItems ||
            !newProject ||
            newProject.__typename !== 'Project'
          ) {
            return
          }

          draft.project.storageItems.meta.nestedProjectsCount += 1
          draft.project.storageItems.meta.totalCount += 1

          draft.project.storageItems.entries.unshift({
            ...newProject,
            sharesCount: storageItems.length,
            nestedProjectsCount: 0,
            previews: [],
          })

          draft.project.storageItems.entries.sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        })

        cache.writeQuery({
          query: GetStorageItemsDocument,
          variables: storageItemsQueryVariables,
          data: newStorageItems,
        })

        // We also want to remove all storage items from the parent project
        storageItems.forEach(item => {
          removeFromStorageItemsList({
            cache,
            target: {
              __typename: item.__typename,
              identifier: item.identifier,
            },
          })
        })
      },
    })
  }

  return dropStorageItemsToNestedProject
}
